import type {
  DashboardKpiItem,
  DashboardProgressFlowItem,
  DashboardReportItem,
  DashboardRole,
  DashboardSummary,
  DashboardWorkloadItem,
} from "@/types/dashboard";

const STORAGE_PREFIX = "dmsuite:dashboard-report-preview";
const STORAGE_TTL_MS = 1000 * 60 * 60 * 6;

export type DashboardReportPreviewAction =
  | "current"
  | "all"
  | "assigned"
  | "specific";

export interface DashboardReportPreviewSnapshot {
  id: string;
  action: DashboardReportPreviewAction;
  generatedAt: string;
  generatedBy: string;
  accountName: string | null;
  scopeLabel: string;
  reportTitle: string;
  headerSubtitle: string;
  filtersLabel: string | null;
  dateRangeLabel: string | null;
  serviceLabel: string | null;
  packageLabel: string | null;
  role: DashboardRole;
  summary: DashboardSummary | null;
  workloadByStatus: DashboardWorkloadItem[];
  progressFlow: DashboardProgressFlowItem[];
  servicePackageKpis: DashboardKpiItem[];
  latestReport: DashboardReportItem | null;
}

export interface DashboardReportPreviewInput {
  action: DashboardReportPreviewAction;
  generatedBy: string;
  accountName: string | null;
  scopeLabel: string;
  reportTitle: string;
  headerSubtitle: string;
  filtersLabel: string | null;
  dateRangeLabel: string | null;
  serviceLabel: string | null;
  packageLabel: string | null;
  role: DashboardRole;
  summary: DashboardSummary | null;
  workloadByStatus: DashboardWorkloadItem[];
  progressFlow: DashboardProgressFlowItem[];
  servicePackageKpis: DashboardKpiItem[];
  latestReport: DashboardReportItem | null;
}

export type DashboardReportSnapshot = DashboardReportPreviewSnapshot;

function createId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function storageKey(id: string): string {
  return `${STORAGE_PREFIX}:${id}`;
}

function safeLocalStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function createDashboardReportPreviewSnapshot(
  input: DashboardReportPreviewInput,
): DashboardReportPreviewSnapshot {
  return {
    id: createId(),
    action: input.action,
    generatedAt: new Date().toISOString(),
    generatedBy: input.generatedBy,
    accountName: input.accountName,
    scopeLabel: input.scopeLabel,
    reportTitle: input.reportTitle,
    headerSubtitle: input.headerSubtitle,
    filtersLabel: input.filtersLabel,
    dateRangeLabel: input.dateRangeLabel,
    serviceLabel: input.serviceLabel,
    packageLabel: input.packageLabel,
    role: input.role,
    summary: input.summary,
    workloadByStatus: input.workloadByStatus,
    progressFlow: input.progressFlow,
    servicePackageKpis: input.servicePackageKpis,
    latestReport: input.latestReport,
  };
}

export function saveDashboardReportPreviewSnapshot(
  snapshot: DashboardReportPreviewSnapshot,
): string {
  const storage = safeLocalStorage();
  if (!storage) {
    return snapshot.id;
  }

  storage.setItem(
    storageKey(snapshot.id),
    JSON.stringify({
      ...snapshot,
      expiresAt: Date.now() + STORAGE_TTL_MS,
    }),
  );

  return snapshot.id;
}

export function loadDashboardReportPreviewSnapshot(
  id: string | null | undefined,
): DashboardReportPreviewSnapshot | null {
  if (!id) return null;

  const storage = safeLocalStorage();
  if (!storage) return null;

  const raw = storage.getItem(storageKey(id));
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as
      | (DashboardReportPreviewSnapshot & { expiresAt?: number })
      | null;

    if (!parsed || (parsed.expiresAt && parsed.expiresAt < Date.now())) {
      storage.removeItem(storageKey(id));
      return null;
    }

    return parsed;
  } catch {
    storage.removeItem(storageKey(id));
    return null;
  }
}

export function buildDashboardReportPreviewUrl(id: string): string {
  const query = new URLSearchParams({ preview: id });
  const base = String(process.env.BASE_URL || "/");
  const normalizedBase = base.endsWith("/") ? base : `${base}/`;
  return `${normalizedBase}reports?dashboardPreview=${encodeURIComponent(id)}&${query.toString()}`;
}

export function removeDashboardReportPreviewSnapshot(id: string): void {
  const storage = safeLocalStorage();
  if (!storage) return;

  storage.removeItem(storageKey(id));
}

export function buildDashboardReportPreviewFilename(
  accountName: string | null | undefined,
): string {
  const base = String(accountName || "dashboard")
    .toLowerCase()
    .replace(/[^a-z0-9]+/gi, "-")
    .replace(/^-+|-+$/g, "");

  return `dashboard-report-${base || "dashboard"}`;
}
